import type { JSX, PropsWithChildren } from 'react';
import { forwardRef } from 'react';
import { Trigger as PopoverTrigger } from '@radix-ui/react-popover';
import { styled } from 'styled-components';
import { ValueSwatch } from './ValueSwatch';
import { ValueNameOrHexCode } from './ValueNameOrHexCode';

export const StyledPopoverTrigger = styled(PopoverTrigger)`
  background-color: var(--wui-color-bg-surface);
  border-radius: var(--wui-border-radius-rounded);
  border: 1px solid var(--wui-color-border);
  padding: var(--wui-space-01);
  transition: background-color var(--wui-motion-duration-03) var(--wui-motion-ease);

  &:hover {
    background-color: var(--wui-color-bg-surface-hover);
  }
`;

const DefaultTriggerContentContainer = styled.div`
  align-items: center;
  display: flex;
  flex-direction: row;
  gap: var(--wui-space-02);
  padding-right: var(--wui-space-02);
`;

const DefaultTriggerContent = (): JSX.Element => {
  return (
    <DefaultTriggerContentContainer>
      <ValueSwatch />
      <ValueNameOrHexCode />
    </DefaultTriggerContentContainer>
  );
};

export type ColorPickerTriggerProps = PropsWithChildren;

/**
 * The button that toggles the color picker popover open and closed. By default
 * it renders a swatch and hex label for the current color; pass custom children
 * to replace that content.
 */
export const ColorPickerTrigger = forwardRef<HTMLButtonElement, ColorPickerTriggerProps>(
  ({ children, ...props }, forwardedRef): JSX.Element => {
    return (
      <StyledPopoverTrigger
        ref={forwardedRef}
        {...props}
      >
        {children ?? <DefaultTriggerContent />}
      </StyledPopoverTrigger>
    );
  },
);

ColorPickerTrigger.displayName = 'ColorPickerTrigger';
